import { useEffect, useState } from "react";
import { divIcon } from "leaflet";
import { Marker, Tooltip, Popup, useMap } from "react-leaflet";
import classes from "./NodeMarker.module.scss";
import { icons } from "../../assets/icons/Icons";

const NodeMarker = ({
  zoomLevel,
  armies,
  node,
  isSelected,
  showArmiesInfo,
  showSoldiers,
  showFriends,
  fontSize,
  showScouts = true,
  selectedArmies,
  setSelectedArmies,
  ...props
}) => {
  const [iconSize, setIconSize] = useState([20, 20]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const map = useMap();

  useEffect(() => {
    switch (zoomLevel) {
      case 5: setIconSize([12, 12]); break;
      case 6: setIconSize([24, 24]); break;
      case 7: setIconSize([48, 48]); break;
      case 8: setIconSize([96, 96]); break;
      case 9: setIconSize([192, 192]); break;
      default: break;
    }
  }, [zoomLevel]);

  const armiesOnNode = armies
    .filter((army) => army.node === node.id)
    .filter((army) => showScouts || army.status !== "scout")
    .sort((a, b) => {
      // Prioritize status order: friend -> enemy -> neutral -> scout
      const statusOrder = {
        friend: 1,
        enemy: 2,
        neutral: 3,
        scout: 4,
      };

      const statusComparison = statusOrder[a.status] - statusOrder[b.status];
      if (statusComparison !== 0) return statusComparison;

      // Sort alphabetically by army name
      return a.name.localeCompare(b.name);
    });

  const handleArmyClick = (army, e) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      // Toggle selection
      setSelectedArmies(prev => {
        const isSelected = prev.some(a => a.id === army.id);
        if (isSelected) {
          return prev.filter(a => a.id !== army.id);
        } else {
          return [...prev, army];
        }
      });
    } else {
      setSelectedArmies([army]);
    }
  };

  const toggleCollapsed = (e) => {
    e.stopPropagation();
    setIsCollapsed((prev) => !prev);
  };

  const locatedScout = armiesOnNode.find((army) => army.status === "scout");
  const visibleArmiesCount = armiesOnNode.filter((army) => army.status !== "scout").length;
  const allySoldiers = armiesOnNode
    .filter((army) => army.status === "friend")
    .reduce((total, army) => total + army.soldiers.length + 1, 0);
  const enemySoldiers = armiesOnNode
    .filter((army) => army.status === "enemy")
    .reduce((total, army) => total + army.soldiers.length + 1, 0);

  const NodeIcon = divIcon({
    html: `
        <svg
        width="${iconSize[0]}px" height="${iconSize[1]}px" viewBox="0 0 30 30" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/2000/svg">
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <circle id="Circle" 
              stroke="${isSelected ? "#2ecc40" : (!locatedScout ? "#03e3fc" : "#fcfc03")}" 
              stroke-width="2" 
              fill="${isSelected ? "rgba(46,204,64,0.3)" : "none"}"
              cx="15" cy="15" r="13"></circle>
          </g>
        </svg>`,
    className: "svg-icon marker",
    iconSize: iconSize,
    iconAnchor: [iconSize[0] / 2, iconSize[1] / 2],
  });

  function isAfterRefresh(dateObj1, dateObj2) {
    // Get the hours and minutes of both dates
    const hours1 = dateObj1.getUTCHours();
    const minutes1 = dateObj1.getUTCMinutes();
    const hours2 = dateObj2.getUTCHours();
    const minutes2 = dateObj2.getUTCMinutes();

    if (
      dateObj1.getUTCFullYear() < dateObj2.getUTCFullYear() ||
      dateObj1.getUTCMonth() !== dateObj2.getUTCMonth()
    ) {
      return true;
    }

    // Check if the dates are on the same day
    if (dateObj1.getUTCDate() === dateObj2.getUTCDate()) {
      // If the dates are on the same day, return true if the first date is before 5:30 and the second date is after 5:30
      return (
        (hours1 < 3 || (hours1 === 3 && minutes1 < 30)) &&
        (hours2 > 3 || (hours2 === 3 && minutes2 >= 30))
      );
    } else {
      // If the dates are on different days, return true if the second date is on the next day after 5:30
      return (
        dateObj2.getUTCDate() !== dateObj1.getUTCDate() &&
        (hours2 > 3 || (hours2 === 3 && minutes2 >= 30))
      );
    }
  }

  return (
    <Marker
      {...props}
      icon={NodeIcon}
      eventHandlers={{
        mouseover: (e) => e.target.openPopup(),
        mouseout: (e) => e.target.closePopup(),
        click: (e) => props.eventHandlers?.click && props.eventHandlers.click(e),
      }}
    >
      <Popup closeButton={false}>
        <div className={classes.popup}>Node: {node.id}</div>
      </Popup>
      {armiesOnNode.length !== 0 && (
        <Tooltip
          className={classes.tooltip}
          direction="bottom"
          permanent
          interactive
          eventHandlers={{
            mouseover: () => map.dragging.disable(),
            mouseout: () => map.dragging.enable(),
          }}
        >
          <div className={classes.tooltipHeader}>
            <button
              type="button"
              className={classes.collapseButton}
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? "Expand" : "Collapse"}
              title={isCollapsed ? "Expand" : "Collapse"}
            >
              {isCollapsed ? <icons.FiChevronDown /> : <icons.FiChevronUp />}
            </button>
            <span className={classes.armyCount}>{armiesOnNode.length}</span>
          </div>
          {!isCollapsed && (
            <>
              {armiesOnNode.map((army) => {
                let flagFaction = `/Images/flags/${army.faction}.png`;
                let flagCountry = `/Images/flags/${army.country}.png`;
                let updatedDate = army
                  ? new Date(army.updated_date).toLocaleString()
                  : null;
                const lastUpdated = new Date(army.updated_date);
                const today = new Date();
                const forUpdate = isAfterRefresh(lastUpdated, today);

                let status;
                switch (army.status) {
                  case "friend":
                    status = forUpdate ? classes.friendForEdit : classes.friend;
                    break;
                  case "neutral":
                    status = forUpdate ? classes.neutralForEdit : classes.neutral;
                    break;
                  case "enemy":
                    status = forUpdate ? classes.enemyForEdit : classes.enemy;
                    break;
                  default:
                    status = classes.neutral;
                    break;
                }
                return (
                  <div
                    key={army.id}
                    className={`
                        ${selectedArmies.some(a => a.id === army.id) ? classes.selectedArmy : classes.army}
                        ${army.status === "scout" ? classes.scout : ""}
                        ${fontSize ? classes.fontSize : ""}
                      `}
                    onClick={(e) => handleArmyClick(army, e)}
                  >
                    {army.status !== "scout" && (
                      <>
                        <img src={flagFaction} alt="" className={zoomLevel === 5 ? classes.smallFlag : classes.flag} />
                        <img src={flagCountry} alt="" className={zoomLevel === 5 ? classes.smallFlag : classes.flag} />
                      </>
                    )}
                    {showArmiesInfo && (
                      <>
                        {army.walls && (
                          <icons.TbBuildingCastle className={`${classes.boolIcon} ${zoomLevel < 6 && classes.bigIcon}`} />
                        )}{" "}
                        {army.ship && (
                          <icons.PiSailboat className={`${classes.boolIcon} ${zoomLevel < 6 && classes.bigIcon}`} />
                        )}{" "}
                        {army.recruitment && (
                          <icons.PiTentDuotone className={`${classes.recruitment} ${zoomLevel < 6 && classes.bigIcon}`} />
                        )}{" "}
                        {zoomLevel >= 6 && (
                          <span className={classes.armyNameTooltip} data-army-name={army.name}>
                            <span className={`${status} ${classes.armyName}`}>
                              {army.name}
                            </span>
                          </span>
                        )}{" "}
                      </>
                    )}
                    {showSoldiers && (showFriends || army.status !== "friend") && (`(${army.soldiers.length + 1})`)}
                    {army.status === "scout" && <icons.BsIncognito />}
                    {army.unsure && (
                      <>
                        <p className={`${classes.unsure} ${zoomLevel < 6 && classes.bigIcon}`}>
                          <icons.TbMapPinOff />
                          <icons.TbMapOff />
                        </p>
                        {showSoldiers && <p className={classes.unsureDate}>{updatedDate.slice(0, -12)}</p>}
                      </>
                    )}
                  </div>
                );
              })}
              {zoomLevel > 5 && visibleArmiesCount > 0 && (
                <div
                  style={{
                    marginTop: "4px",
                    textAlign: "left",
                    fontWeight: "bold",
                  }}
                >
                  {allySoldiers > 0 && (
                    <span style={{ color: "green" }}>
                      Ally soldiers: {allySoldiers}
                    </span>
                  )}

                  {allySoldiers > 0 && enemySoldiers > 0 && (
                    <span style={{ color: "black" }}> / </span>
                  )}

                  {enemySoldiers > 0 && (
                    <span style={{ color: "red" }}>
                      Enemy soldiers: {enemySoldiers}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </Tooltip>
      )}
    </Marker>
  );
};

export default NodeMarker;
